namespace $.$$ {

	export class $shm_hitalama_map_widget_float_entity extends $.$shm_hitalama_map_widget_float_entity {

		private schema_syncing = false
		private field_add_title = ''
		private readonly field_widget_positions = new Map< string, {
			key: string
			value: readonly [ number, number ]
		} >
		private readonly absolute_links = new Map< string, $giper_baza_link >

		dataset() {
			return this.widget().dataset()
		}

		record_count_title() {
			const count = this.dataset().records().length
			return `${ count } ${ count === 1 ? 'запись' : count > 1 && count < 5 ? 'записи' : 'записей' }`
		}

		schema_fields() {
			// Read the list source directly. dataset.fields() can have been first read
			// from a temporary action while it was empty and retain that unrooted cache.
			return this.dataset().Fields()?.remote_list() ?? []
		}

		private absolute_link( key: string, link: string ) {
			let value = this.absolute_links.get( key )
			if( !value || value.str !== link ) this.absolute_links.set( key, value = new $giper_baza_link( link ) )
			return value
		}

		private field_widget_position( field: string, index: number ): readonly [ number, number ] {
			const left = this.left() + 1
			const top = this.top() + this.padding_top() + index * 47
			const key = `${ left }:${ top }`
			const previous = this.field_widget_positions.get( field )
			if( previous?.key === key ) return previous.value
			const value = [ left, top ] as const
			this.field_widget_positions.set( field, { key, value } )
			return value
		}

		schema_widgets_auto() {
			this.schema_fields().map( field => `${ field.link().str }:${ field.title() }` ).join( '|' )
			return this.schema_widgets_sync()
		}

		field_widget_candidates( local_only = false ) {
			const owner = this.widget().link_str()
			const current = this.widget().widgets()
			if( local_only ) return current

			const linked_fields = new Set( current.flatMap( widget => {
				const field = widget.Entity_field()?.val()?.str
				return field ? [ field ] : []
			}) )
			if( this.schema_fields().every( field => linked_fields.has( field.link().str ) ) ) return current

			return [ ... new Map( [
				... current,
				... this.map().Widgets_list()?.remote_list() ?? [],
				... this.map().Widgets_removed_list()?.remote_list() ?? [],
			].filter( widget =>
				widget.type() === 'entity_field'
					&& ( widget.container_widget()?.link_str() === owner
						|| widget.Entity_owner()?.val()?.str === owner )
			).map( widget => [ widget.link_str(), widget ] ) ).values() ]
		}

		@ $mol_action
		schema_widgets_sync( local_only = false ) {
			const dataset = this.dataset()
			const fields = this.schema_fields()
			const candidates = this.field_widget_candidates( local_only )
			const current = this.widget().widgets()
			const field_links = new Set( fields.map( field => field.link().str ) )

			this.schema_syncing = true
			try {
				return $shm_hitalama_map_history.silence( ()=> {
					const desired = fields.flatMap( ( field, index )=> {
						// Each field owns its resumable task. A finished batch item must
						// not remove its wire calls from the next field's replay order.
						const property = $mol_wire_sync( this ).schema_field_sync( field, index, local_only )
						return property ? [ property ] : []
					} )

					for( const property of candidates ) {
						const field = property.Entity_field()?.val()?.str
						if( field && field_links.has( field ) ) continue
						this.map().widget_cut( property.link() )
					}

					const desired_links = desired.map( widget => widget.link_str() )
					if( desired_links.join() !== current.map( widget => widget.link_str() ).join() ) {
						this.widget().widgets( desired )
					}
					const height = Math.max( 100, this.padding_top() + this.padding_bottom()
						+ desired.length * 46 + Math.max( 0, desired.length - 1 ) * this.gap() )
					if( this.widget().bottom_edge_y() !== height ) this.widget().bottom_edge_y( height )
					return desired_links
				} )
			} finally {
				this.schema_syncing = false
			}
		}

		@ $mol_mem_key
		field_widget_initialize( field_link: string ) {
			// The callback identity must survive widget_add suspension. Publish field
			// and owner together with the widget, before any renderer can observe it.
			return ( property: $shm_hitalama_map_widget )=> {
				const property_link = property.link_str()
				const field_ref = this.absolute_link( `${ property_link }:field`, field_link )
				const owner_ref = this.absolute_link( `${ property_link }:owner`, this.widget().link().str )
				if( property.Entity_field()?.val()?.str !== field_link ) property.Entity_field( null )?.val( field_ref )
				if( property.Entity_owner()?.val()?.str !== owner_ref.str ) property.Entity_owner( null )?.val( owner_ref )
			}
		}

		schema_field_sync( field: $shm_hitalama_map_widget_entity_table2_field, index: number, local_only: boolean ) {
			const by_field = new Map( this.field_widget_candidates( local_only ).flatMap( widget => {
				const field = widget.Entity_field()?.val()?.str
				return field ? [[ field, widget ] as const] : []
			}) )
			const field_link = field.link().str
			const configure = ( property: $shm_hitalama_map_widget, attach = false )=> {
				// Configuration belongs to the same resumable operation as creation.
				// Otherwise another schema sync can observe a half-created widget and
				// create a duplicate before its field link has been written.
				this.field_widget_initialize( field_link )( property )
				if( attach && property.container_widget()?.link_str() !== this.widget().link_str() ) property.container_widget( this.widget() )
				if( property.title() !== field.title() ) property.title( field.title() )
				return property
			}
			let property = by_field.get( field_link )
			if( property?.removed() ) this.map().widget_restore( property.link() )
			if( !property ) {
				property = this.map().widget_add_batch(
					`entity_field:${ this.widget().link_str() }:${ field_link }`,
					()=> {
						const created = this.map().widget_add(
							'entity_field',
							this.field_widget_position( field_link, index ),
							Math.max( 1, this.width() - 2 ),
							46,
							`Свойство ${ field_link }`,
							'',
							undefined,
							`entity_field:${ this.widget().link_str() }:${ field_link }`,
							this.field_widget_initialize( field_link ),
						)
						return created ? configure( created, true ) : undefined
					},
				)
			}
			if( !property ) return undefined
			configure( property )
			// An existing detached property is intentional. Schema synchronization creates
			// missing views, but must not silently pull a user-detached view back inside.
			if( property.container_widget()?.link_str() !== this.widget().link_str() ) return undefined
			return property
		}

		links( next?: string[] ) {
			if( next === undefined ) return this.widget().widgets().map( widget => widget.link_str() )
			return $shm_hitalama_map_history.transaction( this.map(), 'Изменение порядка свойств', ()=> {
				this.widget().widgets( next.map( link => this.Map_page().widget_by_link( new $giper_baza_link( link ) ) ) )
				if( this.schema_syncing ) return next

				const dataset = this.dataset()
				const fields = next.flatMap( link => {
					const widget = this.map().widget( link )
					const field = widget.Entity_field()?.remote()
					return field?.dataset()?.link().str === dataset.link().str ? [ field ] : []
				} )
				if( fields.length !== dataset.fields().length ) return next
				if( fields.map( field => field.link().str ).join() === dataset.fields().map( field => field.link().str ).join() ) return next
				dataset.fields_reorder( fields )
				return next
			} )
		}

		link_addable( link: string ) {
			if( this.links().includes( link ) ) return true
			const widget = this.map().widget( link )
			if( widget.type() !== 'entity_field' ) return false
			if( widget.Entity_owner()?.remote()?.link_str() !== this.widget().link_str() ) return false
			return widget.Entity_field()?.remote()?.dataset()?.link().str === this.dataset().link().str
		}

		@ $mol_action
		override move_widgets_to_places(
			links = this.links(),
			skip = new Set< string >(),
		) {
			return $shm_hitalama_map_history.silence( ()=> {
				let top = this.top() + this.padding_top()
				const left = this.left() + this.padding_left()
				const width = this.width() - this.padding_left() - this.padding_right()
				for( const link of links ) {
					const widget = this.Map_page().Widget( new $giper_baza_link( link ) ).Sub()
					if( !skip.has( link ) ) {
						widget.top( top )
						widget.left( left )
						widget.width( width )
						widget.Contextmenu().to_front()
					}
					top += widget.height() + this.gap()
				}
			} )
		}

		@ $mol_action
		field_add( event?: Event | null ) {
			if( event === undefined ) return null
			const dataset = this.dataset()
			if( !this.field_add_title ) {
				this.field_add_title = $shm_hitalama_inc_name(
					this.schema_fields().map( field => field.title() ),
					'Новое свойство',
				)
			}
			if( !this.schema_fields().some( field => field.title() === this.field_add_title ) ) {
				dataset.field_add( this.field_add_title, 'string' )
			}
			this.schema_widgets_sync( true )
			// Keep the name until every possibly suspending operation above has
			// completed. Otherwise a wire restart computes the next name and one click
			// creates two fields.
			this.field_add_title = ''
			return event
		}

		@ $mol_action
		table_open( event?: Event | null ) {
			if( event === undefined ) return null
			const dataset = this.dataset()
			let table = this.map().Widgets_list()?.remote_list().find( widget =>
				widget.type() === 'table2' && widget.Dataset()?.remote()?.link().str === dataset.link().str
			)
			if( !table ) {
				table = this.map().widget_add(
					'table2',
					[ this.left() + this.width() + 40, this.top() ],
					900,
					600,
					this.title(),
					'',
					dataset.link(),
				)
			}
			if( !table ) return event
			this.Map_page().go_to_widget( table.link() )
			this.Map_page().selected_links([ table.link() ])
			return event
		}

	}

}
